import { z } from "zod";
import type { SelectOption as DesignSystemSelectOption } from "@legal-500/legal500-design-system";
import type { SelectOption } from "@/components/Form/SearchableSelect/useSearchableSelect";

export const itemStatusSchema = z.enum(["publish", "pending", "pending_review", "draft", "trash"]);
export const ns = z.preprocess(v => (v == null ? "" : v), z.string());

export const rollingSubmissionFormSchema = z.object({
  submissionStatus: itemStatusSchema,
  visibility: z.enum(["public", "private"]),
  publishedAt: z.string().nullable(),
  companyUuid: z.string().optional(),
  companyName: z.string().optional(),
  firmRef: z.string().optional(),
  officeUuid: z.string().optional(),
  officeName: z.string().optional(),
  publicationId: z.coerce.number().int().positive().optional(),
  publicationName: z.string().optional(),
  practiceAreaId: z.coerce.number().int().positive().optional(),
  practiceAreaName: z.string().optional(),
  contactUuid: z.string().optional(),
  contactName: z.string().optional(),
  teamActive: z.array(
    z.object({
      id: z.string(),
      name: ns,
      role: ns,
      jobTitle: ns,
      email: ns,
      location: ns,
      dateJoined: z.string().nullable(),
      previousFirm: z.string().nullable(),
      created: z.string().nullable(),
      modified: z.string().nullable(),
      modifiedBy: z.string().nullable(),
      status: itemStatusSchema,
    }),
  ),
  teamDeparted: z.array(
    z.object({
      id: z.string(),
      name: ns,
      role: ns,
      jobTitle: ns,
      dateLeft: z.string().nullable(),
      firmJoined: z.string().nullable(),
      created: z.string().nullable(),
      modified: z.string().nullable(),
      modifiedBy: z.string().nullable(),
      status: itemStatusSchema,
    }),
  ),
  keyClients: z.array(
    z.object({
      id: z.string(),
      companyName: ns,
      industry: ns,
      engagementDate: z.string().nullable(),
      publishable: z.boolean(),
      created: z.string().nullable(),
      modified: z.string().nullable(),
      modifiedBy: z.string().nullable(),
      status: itemStatusSchema,
    }),
  ),
  workHighlights: z.array(
    z.object({
      id: z.string(),
      client: ns,
      summary: ns,
      date: z.string().nullable(),
      created: z.string().nullable(),
      modified: z.string().nullable(),
      modifiedBy: z.string().nullable(),
      status: itemStatusSchema,
    }),
  ),
});

export type RollingSubmissionFormValues = z.infer<typeof rollingSubmissionFormSchema>;

export const STATUS_OPTIONS: DesignSystemSelectOption[] = [
  { value: "publish", label: "Publish" },
  { value: "pending", label: "Pending" },
  { value: "draft", label: "Draft" },
  { value: "trash", label: "Trash" },
];

export const SUBMISSION_STATUS_OPTIONS: DesignSystemSelectOption[] = [
  { value: "publish", label: "Published" },
  { value: "pending", label: "Pending" },
  { value: "draft", label: "Draft" },
  { value: "trash", label: "Trash" },
];

export const VISIBILITY_OPTIONS: DesignSystemSelectOption[] = [
  { value: "public", label: "Public" },
  { value: "private", label: "Private" },
];

export const ROLE_OPTIONS: DesignSystemSelectOption[] = [
  { value: "", label: "Please Select" },
  { value: "head_department", label: "Head of department" },
  { value: "partner", label: "Partner" },
  { value: "associate", label: "Associate" },
];

export const PUBLISHABLE_OPTIONS: DesignSystemSelectOption[] = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No (confidential)" },
];

export const INDUSTRY_OPTIONS_FALLBACK: SelectOption[] = [
  { value: "", label: "Select industry/sector" },
];

export const ROLE_LABELS: Record<string, string> = Object.fromEntries(
  ROLE_OPTIONS.filter(o => o.value !== "").map(o => [o.value, o.label]),
);

export function getInitials(name: string | null | undefined): string {
  if (!name) return "";
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

export function getPendingCount(items: Array<{ status: string }>): number {
  return items.filter(i => i.status === "pending" || i.status === "pending_review").length;
}
